import React, { FC, useEffect, useRef, useState } from 'react';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { IAccount } from '../../../store/merchnts/merchant-interfaces';
import { Title } from '../../developers/page-components/Title';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import { BUTTON_TYPE, ButtonComponent } from '../../ButtonComponent';
import { SearchInputSmall } from '../../common/SearchInputSmall';
import { IMerchantData } from '../../../interfaces/merchant';
import { useNavigate } from 'react-router-dom';

const scrollToSelectedElement = (value: string, position?: ScrollLogicalPosition): void => {
	const div = document.getElementById(value);
	div?.scrollIntoView({
    behavior: 'smooth',
    block: position ? position : 'center',
    inline: 'start',
  });
};

interface IFormattedMerchant {
	merchantId: string;
	merchantName: string;
	dba: string;
	account: string;
	accountId: string;
}

export const getAllMerchants = (accountList: IAccount[]): IFormattedMerchant[] => {
	const allMerchants: IFormattedMerchant[] = [];
	accountList?.forEach(account => {
		if (account?.merchants?.length) {
			account?.merchants?.forEach(merchant => {
				const formattedMerchant = {
					merchantId: merchant?.id,
					merchantName: merchant?.companyName,
					dba: merchant?.dba,
					account: account?.name,
					accountId: account?.id,
				};
				allMerchants.push(formattedMerchant);
			});
		}
	});
	return allMerchants;
};

interface Props {
	results: IFormattedMerchant[];
	onSelect: (value: any) => void;
}

const Dropdown: FC<Props> = (props) => {
	const { results, onSelect } = props;
	const dropdownRef = useRef(null);
	const [selectedDropdownItemIndex, setSelectedDropdownItemIndex] = useState<number>(0);
	const [selectedDropdownItem, setSelectedDropdownItem] = useState<IFormattedMerchant>(null);

	useEffect(() => {
		if (dropdownRef.current) {
			dropdownRef.current.focus();
		}
		dropdownRef.current.scrollTop = 0;
		setSelectedDropdownItemIndex(0);
		setSelectedDropdownItem(results[0]);
	}, [results]);

	const handleDropdownKeyDown = (e): void => {
		if (e.key === ' ') {
			e.preventDefault();
		}
		if (e.key === 'ArrowUp' && selectedDropdownItemIndex > 0) {
			const newItem = results?.find((el, index) => index === selectedDropdownItemIndex-1);
			setSelectedDropdownItem(newItem);
			setSelectedDropdownItemIndex(selectedDropdownItemIndex-1);
			scrollToSelectedElement(newItem?.merchantId, 'nearest');
		}
		if (e.key === 'ArrowDown' && selectedDropdownItemIndex < results?.length-1) {
			const newItem = results?.find((el, index) => index === selectedDropdownItemIndex+1);
			setSelectedDropdownItem(newItem);
			setSelectedDropdownItemIndex(selectedDropdownItemIndex+1);
			scrollToSelectedElement(newItem?.merchantId, 'nearest');
		}
		if (e.key === 'Enter') {
			onSelect(selectedDropdownItem);
		}

	};
	return (
		<div
			className="position-absolute mx-8 py-3 dropdown-container account-body-container container-outline"
			ref={dropdownRef}
			onKeyDown={handleDropdownKeyDown}
			tabIndex={0}
		>
			{results.map(item => (
				<div
					className={clsx(
						'd-flex align-items-center cursor-pointer ps-3 py-3',
						selectedDropdownItem?.merchantId === item?.merchantId ? 'active-account' : 'selector-account',
					)}
					onClick={(): void => onSelect(item)}
					key={item?.merchantId}
				>
					<span>{item?.account}</span>
					<FontAwesomeIcon icon={faChevronRight} className="mx-1 h-10px" width={10}/>
					<div>
						<div>{item?.dba ? item?.dba : item?.merchantId}</div>
						{Boolean(item?.merchantName) && <div className="fc-text-overflow fs-8">({item?.merchantName})</div>}
					</div>
				</div>
			))}

		</div>
	);
};

interface AccountSelectorModalProps {
	setSelectedValue: (value: string) => void,
	merchantId: string,
	setMerchantId: (value: string) => void,
	closeModal: () => void
}

export const AccountSelectorModal: FC<AccountSelectorModalProps> = ({ setSelectedValue, merchantId, setMerchantId, closeModal }) => {
	const isMounted = useRef(false);
	const merchantColumnRef = useRef(null);
	const accountColumnRef = useRef(null);
	const inputRef = useRef(null);
	const navigate = useNavigate();

	const { merchantState: { accountList }} = useMerchants();

	const [selectedAccount, setSelectedAccount] = useState<IAccount>(accountList[0]);
	const [selectedMerchant, setSelectedMerchant] = useState<IMerchantData>(accountList[0]?.merchants[0]);
	const [searchValue, setSearchValue] = useState<string>('');
	const [showDropdown, setShowDropdown] = useState<boolean>(false);
	const [filteredResults, setFilteredResults] = useState<IFormattedMerchant[]>(null);
	const [selectedAccountIndex, setSelectedAccountIndex] = useState<number>(0);
	const [selectedMerchantIndex, setSelectedMerchantIndex] = useState<number>(0);

	const setAccount = (account: IAccount, index: number): void => {
		setSelectedAccount(account);
		setSelectedAccountIndex(index);
		setSelectedMerchantIndex(0);
		if (account?.merchants?.length) {
			setSelectedMerchant(account?.merchants[0]);
		} else {
			setSelectedMerchant(null);
		}
	};

	const setMerchant = (merchant: IMerchantData, index: number): void => {
		setSelectedMerchant(merchant);
		setSelectedMerchantIndex(index);
	};

	const setFilteredAccount = (item: IFormattedMerchant): void => {
		const account = accountList?.find(account => item?.account === account?.name);
		const filteredMerchant = account?.merchants?.find(el => el.id === item?.merchantId);
		setSelectedAccount(account);
		setSelectedMerchant(filteredMerchant);
		setShowDropdown(false);
		setTimeout(() => {
			scrollToSelectedElement( item?.merchantId);
		}, 10);
	};

	useEffect(() => {
		if (!isMounted.current) return;
		if (searchValue) {
			const foundMerchants = getAllMerchants(accountList)?.filter(merchant => {
				return merchant?.merchantName?.toLowerCase()?.includes(searchValue?.toLowerCase()?.trim())
					|| merchant?.merchantId?.toLowerCase()?.includes(searchValue?.toLowerCase()?.trim())
					|| merchant?.dba?.toLowerCase()?.includes(searchValue?.toLowerCase()?.trim());
			});
			if (foundMerchants.length > 1) {
				setShowDropdown(true);
				setFilteredResults(foundMerchants);
			} else {
				setFilteredAccount(foundMerchants[0]);
			}
		} else {
			setShowDropdown(false);
		}
	}, [searchValue]);

	useEffect(() => {
		if (merchantId && !isMounted.current) {
			const filteredMerchant = getAllMerchants(accountList)?.find(merchant => merchant?.merchantId === merchantId);
			const savedAccount = accountList?.find(account => account?.id === filteredMerchant?.accountId);
			const savedMerchant = savedAccount?.merchants?.find((merchant, index) => {
				if (merchant?.id === merchantId) {
					setSelectedMerchantIndex(index);
					return merchant;
				}
			});
			setSelectedMerchant(savedMerchant);

			scrollToSelectedElement(merchantId);
		} else if (selectedMerchant) {
			scrollToSelectedElement(selectedMerchant?.id);
		}
	}, [selectedAccount]);

	useEffect(() => {
		if (merchantId) {
			const savedMerchant = getAllMerchants(accountList)?.find(merchant => merchant?.merchantId === merchantId);
			const savedAccount = accountList?.find(account => account?.id === savedMerchant?.accountId);
			setSelectedAccount(savedAccount);

			scrollToSelectedElement(savedAccount?.id);
			merchantColumnRef.current.focus();
		} else {
			accountColumnRef.current.focus();
		}
	}, []);

	useEffect(() => {
		if (!isMounted.current) {
			isMounted.current = true;
		}
	}, []);

	const handleSelect = (selectedItem: IFormattedMerchant): void => {
		setSearchValue('');
		setFilteredAccount(selectedItem);
		merchantColumnRef.current.focus();
	};

	const handleSave = (): void => {
		localStorage.setItem('chosenMerchant', selectedMerchant?.id);
		setSelectedValue(selectedMerchant?.dba ? selectedMerchant?.dba : selectedMerchant?.id);
		setMerchantId(selectedMerchant?.id);
		closeModal();
	};

	const handleSubmit = (e): void => {
		e.preventDefault();
		// if (!selectedMerchant) return;
		handleSave();
	};

	const handleAccountKeyDown = (e): void => {
		if (e.key === 'ArrowUp' && selectedAccountIndex > 0) {
			const newAccount = accountList?.find((el, index) => index === selectedAccountIndex-1);
			setAccount(newAccount, selectedAccountIndex -1);
			scrollToSelectedElement(newAccount?.id, 'nearest');
		} else if (e.key === 'ArrowDown' && selectedAccountIndex < accountList.length -1) {
			const newAccount = accountList?.find((el, index) => index === selectedAccountIndex+1);
			setAccount(newAccount, selectedAccountIndex +1);
			scrollToSelectedElement(newAccount?.id, 'nearest');
		} else if (e.key === 'Tab' && selectedAccount?.merchants?.length) {
				e.preventDefault();
				merchantColumnRef.current.focus();
		} else if (e.key === 'Tab') {
			e.preventDefault();
			accountColumnRef.current.focus();
		}
	};

	const handleMerchantKeyDown = (e): void => {
		if (selectedAccount?.merchants?.length === 0) return;
		if (e.key === 'ArrowUp' && selectedMerchantIndex > 0) {
			const newMerchant = selectedAccount?.merchants?.find((el, index) => index === selectedMerchantIndex-1);
			setSelectedMerchant(newMerchant);
			setSelectedMerchantIndex(selectedMerchantIndex-1);
			scrollToSelectedElement(newMerchant?.id, 'nearest');
		} else if (e.key === 'ArrowDown' && selectedMerchantIndex < selectedAccount?.merchants?.length -1) {
			const newMerchant = selectedAccount?.merchants?.find((el, index) => index === selectedMerchantIndex+1);
			setSelectedMerchant(newMerchant);
			setSelectedMerchantIndex(selectedMerchantIndex+1);
			scrollToSelectedElement(newMerchant?.id, 'nearest');
		} else if (e.key === 'Tab') {
			e.preventDefault();
			accountColumnRef.current.focus();
		}
	};

	const handleFormKeyDown = (e): void => {
		const isLetterOrNumber = /^[a-zA-Z0-9\s]$/.test(e.key);
		if (isLetterOrNumber || e.key === 'Backspace') {
			inputRef.current.focus();
		}

		if (e.key === 'Enter' && selectedMerchant && !showDropdown) {
			handleSave();
		}
	};

	const navigateToMerchantDetails = (e: React.MouseEvent<SVGSVGElement, MouseEvent>, mid: string): void => {
		e.stopPropagation();
		closeModal();
		navigate(`/accounts/merchants/${mid}/business-information`);
	};

	return (
		<form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} tabIndex={0}>
			<div className="px-8 position-relative">
				<div className="application-selector mb-5 account-selector">
					<SearchInputSmall
						placeholder="Search merchant"
						searchValue={searchValue}
						setSearchValue={setSearchValue}
						inputRef={inputRef}
						isGreyBG
					/>
				</div>
				{showDropdown && (
					<Dropdown
						results={filteredResults}
						onSelect={handleSelect}
					/>
				)}
			</div>

			<div className="d-flex px-8 pb-5">
				<div
					className="col-6 me-2 container-outline"
					onKeyDown={handleAccountKeyDown}
					tabIndex={0}
					ref={accountColumnRef}
				>
					<Title title="Accounts" additionalClass="fs-6 fw-bolder mb-2"/>
					<div className="account-body-container pe-2">
						{accountList.map((account, index) => {
							const { id, name, merchants } = account;
							return (
								<div
									key={id}
									id={id}
									className={clsx(
										'd-flex align-items-center justify-content-between fs-7 px-3 py-2 mt-2',
										selectedAccount?.id === id ? 'active-account' : 'selector-account',
										merchants?.length ? 'cursor-pointer' : 'cursor-not-allowed'
									)}
									onClick={(): void => setAccount(account, index)}
								>
									<div className="cursor-pointer">{name}</div>
									<div className="d-flex align-items-center icon-color">
										<span className="me-2">{merchants?.length}</span>
										<FontAwesomeIcon icon={faChevronRight}/>
									</div>

								</div>
							);
						})}
					</div>
				</div>

				<div
					className="col-6 container-outline"
					onKeyDown={handleMerchantKeyDown}
					tabIndex={0}
					ref={merchantColumnRef}
				>
					<Title title="Merchants" additionalClass="fs-6 fw-bolder mb-2"/>
					<div className="account-body-container pe-2">
						{
							selectedAccount && (
								selectedAccount?.merchants.map((el: IMerchantData, index) => (
									<div
										key={el?.id}
										id={el?.id}
										className={clsx(
											'px-3 py-2 mt-2 cursor-pointer d-flex align-items-center justify-content-between',
											selectedMerchant?.id === el?.id ? 'active-account' : 'selector-account',
											!el?.companyName && 'd-flex align-items-center'
										)}
										onClick={(): void => setMerchant(el, index)}
									>
										<div>
											<div className="fc-text-overflow fs-7">{el?.dba ? el?.dba : el?.id}</div>
											{Boolean(el?.companyName) && <div className="fc-text-overflow fs-8">({el?.companyName})</div>}
										</div>

										<FontAwesomeIcon
											icon={faArrowUpRightFromSquare}
											className="w-16px icon-color"
											onClick={(e): void => navigateToMerchantDetails(e, el?.id)}
										/>
									</div>
								))
							)
						}
					</div>
				</div>
			</div>

			<div className="separator justify-content-between"/>

			<div className={clsx(
				'px-8 py-5 d-flex align-items-center',
					selectedAccount ? 'justify-content-between' : 'justify-content-end'
				)}
			>
				<div className="text-danger d-flex align-items-center fs-7">
					<span>{selectedAccount?.name}</span>
					{selectedAccount && <FontAwesomeIcon icon={faChevronRight} className="mx-1 h-10px" width={10}/>}
					<span>{selectedMerchant?.dba ? selectedMerchant?.dba : selectedMerchant?.id}</span>
				</div>
				<div className="w-175px">
					<ButtonComponent
						buttonName="Close"
						buttonType={BUTTON_TYPE.CANCELLED}
						additionalClass="no-letter-spacing me-5 px-3 py-2"
						onButtonClick={closeModal}
					/>
					<ButtonComponent
						buttonName="Switch view"
						additionalClass="no-letter-spacing px-3 py-2"
						type='submit'
						// onButtonClick={(): void => selectedMerchant && handleSave()}
						isDisabled={!selectedMerchant}
					/>
				</div>
			</div>
		</form>
	);
};
